import { Card, DatePicker, Space, Typography, Button, Tag } from 'antd'
import { ClockCircleOutlined, CalendarOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import 'dayjs/locale/sr'

const { Text } = Typography

dayjs.locale('sr')

interface SchedulePickerProps {
  value?: Dayjs | null
  onChange?: (value: Dayjs | null) => void
  timezone?: string
}

// Best posting times (based on general social media research)
const bestTimes = [
  { label: '9:00', description: 'Jutro' },
  { label: '12:00', description: 'Rucak' },
  { label: '17:00', description: 'Posle posla' },
  { label: '20:00', description: 'Vece' },
]

// Quick schedule options
const quickOptions = [
  { label: 'Za 1 sat', getValue: () => dayjs().add(1, 'hour').startOf('hour') },
  { label: 'Sutra 9:00', getValue: () => dayjs().add(1, 'day').hour(9).minute(0).second(0) },
  { label: 'Sutra 17:00', getValue: () => dayjs().add(1, 'day').hour(17).minute(0).second(0) },
  { label: 'Ponedeljak 9:00', getValue: () => dayjs().day(8).hour(9).minute(0).second(0) }, // Next Monday
]

export function SchedulePicker({
  value,
  onChange,
  timezone = 'Europe/Belgrade',
}: SchedulePickerProps) {
  // Set time keeping the date
  const setTime = (hours: number, minutes: number = 0) => {
    const baseDate = value || dayjs()
    const newValue = baseDate.hour(hours).minute(minutes).second(0)
    onChange?.(newValue)
  }

  // Apply quick option
  const applyQuickOption = (getValue: () => Dayjs) => {
    onChange?.(getValue())
  }

  // Clear schedule
  const clearSchedule = () => {
    onChange?.(null)
  }

  return (
    <Card title="Zakazivanje" size="small">
      <Space direction="vertical" style={{ width: '100%' }} size={16}>
        {/* Date and time picker */}
        <DatePicker
          showTime={{ format: 'HH:mm', minuteStep: 15 }}
          format="DD.MM.YYYY HH:mm"
          value={value}
          onChange={onChange}
          style={{ width: '100%' }}
          placeholder="Izaberi datum i vreme"
          disabledDate={(current) => current && current < dayjs().startOf('day')}
          showNow={false}
        />

        {/* Quick options */}
        <div>
          <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
            <ClockCircleOutlined /> Brzo zakazivanje
          </Text>
          <Space wrap size={4}>
            {quickOptions.map((option) => (
              <Button
                key={option.label}
                size="small"
                onClick={() => applyQuickOption(option.getValue)}
              >
                {option.label}
              </Button>
            ))}
          </Space>
        </div>

        {/* Best times */}
        <div>
          <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
            <CalendarOutlined /> Najbolja vremena
          </Text>
          <Space wrap size={4}>
            {bestTimes.map((time) => (
              <Tag
                key={time.label}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  const [hours, minutes] = time.label.split(':').map(Number)
                  setTime(hours, minutes)
                }}
              >
                {time.label} - {time.description}
              </Tag>
            ))}
          </Space>
        </div>

        {/* Selected time display */}
        {value && (
          <div
            style={{
              padding: 12,
              backgroundColor: '#f6ffed',
              borderRadius: 8,
              border: '1px solid #b7eb8f',
            }}
          >
            <Space direction="vertical" size={4}>
              <Text strong>
                <CalendarOutlined style={{ marginRight: 8 }} />
                {value.format('dddd, D. MMMM YYYY')}
              </Text>
              <Text>
                <ClockCircleOutlined style={{ marginRight: 8 }} />
                {value.format('HH:mm')} ({timezone})
              </Text>
              <Button
                type="link"
                size="small"
                danger
                onClick={clearSchedule}
                style={{ padding: 0 }}
              >
                Otkazi zakazivanje
              </Button>
            </Space>
          </div>
        )}

        {/* Timezone note */}
        <Text type="secondary" style={{ fontSize: 11 }}>
          Sva vremena su u {timezone} vremenskoj zoni
        </Text>
      </Space>
    </Card>
  )
}

export default SchedulePicker
